import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Brain,
  Sparkles,
  RefreshCw,
  Phone,
  PhoneOff,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  TrendingUp,
  AlertTriangle,
  Copy,
  Check
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authAxios = axios.create();
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUS_ICONS = {
  "ARANMAYAN DATA": { icon: PhoneOff, color: "text-orange-500", bg: "bg-orange-50" },
  "CEVAP YOK": { icon: Phone, color: "text-blue-500", bg: "bg-blue-50" },
  "DÜŞÜNECEK": { icon: Clock, color: "text-amber-500", bg: "bg-amber-50" },
  "KAYIT OK": { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50" },
  "KAPALI": { icon: XCircle, color: "text-red-500", bg: "bg-red-50" },
};

const AIAnalysis = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`${API}/ai/crm-summary`);
      setSummary(res.data);
    } catch (err) {
      toast.error("Veri ozeti yuklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    setAiResult(null);
    try {
      const res = await authAxios.post(`${API}/ai/smart-analysis`);
      setAiResult(res.data);
      toast.success("AI analiz tamamlandi!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI analiz hatasi");
    } finally {
      setAnalyzing(false);
    }
  };

  const copyToClipboard = () => {
    if (aiResult?.analysis) {
      navigator.clipboard.writeText(aiResult.analysis);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-analysis">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Brain size={24} className="text-purple-600" />
            AI Veri Analizi
          </h2>
          <p className="text-sm text-slate-500 mt-1">CRM verilerinizi analiz edip aksiyonel oneriler sunar</p>
        </div>
        <Button
          onClick={runAnalysis}
          disabled={analyzing}
          className="bg-purple-600 hover:bg-purple-700 text-white px-6"
          data-testid="run-analysis-btn"
        >
          {analyzing ? (
            <><RefreshCw className="animate-spin mr-2" size={16} /> Analiz ediliyor...</>
          ) : (
            <><Sparkles className="mr-2" size={16} /> Analiz Baslat</>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg"><Users size={20} className="text-indigo-500" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.total_leads}</p>
                <p className="text-xs text-slate-500">Toplam Data</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg"><TrendingUp size={20} className="text-green-500" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.recent_7d}</p>
                <p className="text-xs text-slate-500">Son 7 Gun</p>
              </div>
            </CardContent>
          </Card>
          {summary.status_distribution?.slice(0, 2).map(s => {
            const si = STATUS_ICONS[s.status] || { icon: Phone, color: "text-slate-500", bg: "bg-slate-50" };
            const IconComp = si.icon;
            return (
              <Card key={s.status} className="bg-white border-slate-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`p-2 ${si.bg} rounded-lg`}><IconComp size={20} className={si.color} /></div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{s.count}</p>
                    <p className="text-xs text-slate-500 truncate max-w-[120px]">{s.status}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Status Distribution */}
      {summary?.status_distribution && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-600">Durum Dagilimi</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {summary.status_distribution.map(s => {
                const si = STATUS_ICONS[s.status] || { icon: Phone, color: "text-slate-400", bg: "bg-slate-50" };
                const IconComp = si.icon;
                const pct = summary.total_leads > 0 ? ((s.count / summary.total_leads) * 100).toFixed(1) : 0;
                return (
                  <div key={s.status} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition">
                    <IconComp size={14} className={si.color} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-600 truncate">{s.status}</p>
                      <div className="flex items-center gap-1">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-400 rounded-full" style={{width: `${pct}%`}} />
                        </div>
                        <span className="text-[10px] font-medium text-slate-500">{s.count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agent Details */}
      {summary?.agent_details && summary.agent_details.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-600">Danisma Bazinda Dagılım</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left p-2 text-xs text-slate-500 font-medium">Danisma</th>
                    <th className="text-center p-2 text-xs text-slate-500 font-medium">Toplam</th>
                    <th className="text-center p-2 text-xs text-orange-500 font-medium">Aranmayan</th>
                    <th className="text-center p-2 text-xs text-blue-500 font-medium">Cevap Yok</th>
                    <th className="text-center p-2 text-xs text-amber-500 font-medium">Dusunecek</th>
                    <th className="text-center p-2 text-xs text-emerald-500 font-medium">Kayit OK</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.agent_details.map(a => (
                    <tr key={a.agent_name} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="p-2 text-slate-700 font-medium">{a.agent_name}</td>
                      <td className="p-2 text-center font-semibold text-slate-800">{a.total}</td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs ${(a.status_breakdown?.["ARANMAYAN DATA"] || 0) > 0 ? "bg-orange-100 text-orange-700 font-medium" : "text-slate-400"}`}>
                          {a.status_breakdown?.["ARANMAYAN DATA"] || 0}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <span className="text-xs text-blue-600">{a.status_breakdown?.["CEVAP YOK"] || 0}</span>
                      </td>
                      <td className="p-2 text-center">
                        <span className="text-xs text-amber-600">{a.status_breakdown?.["DÜŞÜNECEK"] || 0}</span>
                      </td>
                      <td className="p-2 text-center">
                        <span className={`text-xs ${(a.status_breakdown?.["KAYIT OK"] || 0) > 0 ? "text-emerald-600 font-medium" : "text-slate-400"}`}>
                          {a.status_breakdown?.["KAYIT OK"] || 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Analysis Result */}
      {analyzing && (
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-8 flex flex-col items-center justify-center gap-3">
            <RefreshCw className="animate-spin text-purple-500" size={32} />
            <p className="text-purple-700 font-medium">AI verilerinizi analiz ediyor...</p>
            <p className="text-purple-500 text-sm">Bu islem 15-30 saniye surebilir</p>
          </CardContent>
        </Card>
      )}

      {aiResult && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-white border-purple-200" data-testid="ai-result">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
                  <Sparkles size={20} className="text-purple-500" />
                  AI Analiz Sonucu
                </CardTitle>
                <Button variant="outline" size="sm" onClick={copyToClipboard} className="border-slate-300 text-slate-600">
                  {copied ? <><Check size={14} className="mr-1" /> Kopyalandi</> : <><Copy size={14} className="mr-1" /> Kopyala</>}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="ai-analysis-text">
                {aiResult.analysis}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default AIAnalysis;
